<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  import Tabs from "$lib/components/Tabs/Tabs.svelte";
  import Chart from "$lib/components/Chart/Chart.svelte";
  import Map from "$lib/components/Map/Map.svelte";
  import { Block } from "@urbaninstitute/dataviz-components";
  import { createToolData } from "$utils/data.svelte.js";
  import { onMount } from "svelte";
  import { DEFAULT_PLAN_TYPE } from "$utils/consts.js";

  const toolData = createToolData();

  onMount(() => {
    toolData.fetchChartData(toolData.activeGeo);
  });

  // Fetch new chart data when geography changes
  $effect(() => {
    const geo = toolData.activeGeo;
    toolData.fetchChartData(geo);
  });

  // Fetch map data on demand when map tab is activated, and when frequency changes
  $effect(() => {
    if (toolData.mapDataNeeded) {
      const freq = toolData.getMapFrequency();
      toolData.fetchMapData(freq);
    }
  });

  $effect(() => {
    // check if selection is made with unavailable plan type and revert to default
    if (!toolData.availablePlans.includes(toolData.activeUtilization)) {
      toolData.activeUtilization = DEFAULT_PLAN_TYPE;
    }
  });
</script>

<Block>
  <h4 class="tool-subhead">
    Medicaid-Covered Prescription Fills for Medications to Treat Nicotine and Tobacco Dependence
  </h4>
</Block>
<Block width="wide">
  <div class="tool-wrapper">
    <Tabs
      onTabChange={(tab) => {
        if (tab === "map") toolData.mapDataNeeded = true;
      }}
    >
      {#snippet chart()}
        <Chart />
      {/snippet}
      {#snippet map()}
        <Map />
      {/snippet}
    </Tabs>
  </div>
</Block>
<Block>
  <div class="tool-note">
    <p class="note">
      <strong>Source:</strong>
      <a
        href="https://www.medicaid.gov/medicaid/prescription-drugs/state-drug-utilization-data"
        target="_blank">Medicaid State Drug Utilization Data</a
      >, 2019 to 2025;
      <a
        href="https://www.samhsa.gov/data/data-we-collect/nsduh-national-survey-drug-use-and-health/national-releases"
        target="_blank">National Survey on Drug Use and Health</a
      >, 2021 to 2022;
      <a
        href="https://www.cdc.gov/nchs/nhis/index.html"
        target="_blank">National Health Interview Survey</a
      >, 2019 to 2023;
      <a
        href="https://www.medicaid.gov/medicaid/program-information/medicaid-chip-enrollment-data/medicaid-and-chip-enrollment-trend-snapshot"
        target="_blank"
        >Centers for Medicare & Medicaid Services Performance Indicator Medicaid Enrollment Data</a
      >, 2019 to 2025.
    </p>
    <p class="note">
      <strong>Notes:</strong> NRT = nicotine replacement therapy (e.g., Nicorette and generics), which
      helps people with cessation through controlled doses of nicotine without exposure to harmful chemicals.
      Non-nicotine medications include those such as varenicline (e.g., Chantix and generics) and bupropion
      (e.g., Zyban and generics). Some values are imputed because the source data suppress prescription
      fill counts when fewer than 11 fills are reported for a prescription, year/quarter, state, and
      plan type. If all quarters of data in which a prescription was present are suppressed, we set the
      number of fills to zero for the year. If at least one quarter of data is not suppressed, we set
      the number of fills to five for all suppressed quarters and adjust to align with national totals.
      Estimates for bupropion are scaled by adjustment factors derived from Medicaid claims that identify
      the share of bupropion prescriptions for nicotine dependence treatment versus other indications.
    </p>
  </div>
</Block>

<style>
  .note {
    font-size: var(--font-size-sm) !important;
    margin-top: var(--spacing-8);
    margin-bottom: 0 !important;
  }
  .note:not(:first-of-type) {
    margin-top: 0;
  }
  h4.tool-subhead {
    font-size: 24px !important;
  }
  @media (min-width: 768px) {
    h4.tool-subhead {
      font-size: 28px !important;
    }
  }
</style>
